// The org role matrix, as a pure function so it can be tested without React.
//
// It mirrors the router's `roleAllows` (`src/tools/authz.go`) — the router is authoritative and
// this only gates UI controls, so a Member never clicks into a guaranteed refusal.
//
// ⚠⚠ IT LIVES HERE, AND NOT IN THE HOOK, BECAUSE A TIER DRIFTED SILENTLY AND NOTHING CAUGHT IT.
// `useOrgRole` imports the organizations store, which pulls in zustand and supabase, so the matrix
// could not be exercised by `node --experimental-strip-types` and had no test at all. app#168 was
// the cost: rename stayed at Member here while the database moved it to Admin, and the only thing
// that noticed was a customer getting a raw Postgres error.

export type OrgRole = 'owner' | 'admin' | 'member' | null

export type OrgAction =
  | 'view'
  | 'node.write'
  | 'node.storage'
  | 'service.unauthenticated'
  | 'network.create'
  | 'members.manage'
  | 'support.manage'
  | 'billing.manage'
  | 'sso.manage'

/**
 * Whether `role` may perform `action` in the active org.
 *
 * Any member may view, run the node LIFECYCLE (`node.write`: register, provision) and create a
 * network, which they then own (`network.create`; docs/PERMISSIONS.md § 5 Network, decided Q-3,
 * app#376). Member management, the org's "Allow noBGP support" setting and its temporary grant
 * (`support.manage`, § 8), the node storage area and a PUBLIC proxy need Admin/Owner; billing and
 * SSO are Owner only. ⚠ `billing.manage` is also the gate for every READ of money data (invoices,
 * card, credit balance, spend cap; § 5 Org): usage is any member, money is the Owner.
 *
 * ⚠⚠ WHAT A MEMBER MAY DO TO ONE NODE, NETWORK OR SERVICE IS NOT A ROLE (docs/PERMISSIONS.md § 4
 * and § 5, app#373). Rename, channel, remove, stop compute, publish or change a service, answer an
 * access request: each belongs to the node's OWNER or the network's OWNER, or an org Owner/Admin.
 * Ask `mayManageNode` / `mayManageNetwork` / `mayChangeService` below, never `allows`. The
 * `node.rename` action that lived here said "Admin only" and hid the rename from the node's own
 * Member owner; `service.write` and `command.run` said "any Member" and no component read them.
 *
 *   * `node.storage` — `AuthorizeNodeStorage` (router `src/tools/storage.go`) requires Admin/Owner
 *     because nothing else stands in front of a write there. Every other node operation has an
 *     agent-side veto behind it; this one does not, so the role IS the whole gate.
 *     ⚠ The NETWORK share is a different tree with a different rule and must not take this check.
 *
 *   * `service.unauthenticated` — a PUBLIC proxy (`auth_required: false`). The router refuses it
 *     to anyone but an org Owner/Admin (`ActionServiceUnauthenticated`), and a terminal is never
 *     public for anyone (PERMISSIONS.md § 5 Services).
 */
export const allows = (role: OrgRole, action: OrgAction): boolean => {
  switch (action) {
    case 'view':
    case 'node.write':
    case 'network.create':
      return !!role // any member
    case 'node.storage':
    case 'service.unauthenticated':
    case 'members.manage':
    case 'support.manage':
      return role === 'owner' || role === 'admin'
    case 'billing.manage':
    case 'sso.manage':
      return role === 'owner'
    default:
      return false
  }
}

/**
 * Whether `role`, as the signed-in user `userId`, controls a resource whose owner is `ownerId`:
 * the resource's OWNER, or an org Owner/Admin. Nobody else (docs/PERMISSIONS.md § 4 and § 5).
 *
 * The router asks the same rule at every door (`requireCreatorOrAdmin`). This only hides controls.
 *
 * ⚠ A null role is never allowed, even for the owner. The router reaches the object only through
 * the caller's org membership, so an owner who has left the org controls nothing.
 * ⚠ A missing `ownerId` or `userId` is never a match: `null === null` must not make a stranger
 * the owner.
 */
const controls = (
  role: OrgRole,
  ownerId: string | null | undefined,
  userId: string | null | undefined
): boolean => {
  if (!role) return false
  if (role === 'owner' || role === 'admin') return true
  return !!ownerId && !!userId && ownerId === userId
}

/**
 * Whether `role` may REMOVE a node or a network whose owner is `ownerId` (app#371, Benoit
 * 2026-09-23). The owner is `nodes.owner_user_id` / `networks.owner_id`. The router's
 * `node_delete` and `network_delete` and router migration `0212` carry the same rule.
 */
export const mayRemove = controls

/**
 * Whether `role` may manage a NODE: rename it, set its channel, remove it, and stop, extend or
 * terminate its compute (PERMISSIONS.md § 4 "control", § 5 Node). `nodeOwnerId` is
 * `nodes.owner_user_id`. Router: `node_rename`, `node_delete`, the task tools; migration `0229`.
 */
export const mayManageNode = controls

/**
 * Whether `role` may manage a NETWORK: rename it, set its channel, manage its enrollment keys
 * (PERMISSIONS.md § 5 Network). `networkOwnerId` is `networks.owner_id`. Router: `network_update`,
 * the `network_key_*` tools.
 */
export const mayManageNetwork = controls

/**
 * Whether `role` may publish, change or delete a service on a node, and change or answer who may
 * visit it (PERMISSIONS.md § 5 Services: "the node's controllers"). A service has no owner of its
 * own: it belongs to its node, so `nodeOwnerId` is the NODE's `owner_user_id`. Router:
 * `service_publish` / `service_update` / `service_delete` / `service_share`.
 */
export const mayChangeService = controls

/**
 * Whether the signed-in user `userId`, with `role` in the node's org, is a CONTROLLER of a node
 * whose owner is `ownerId` (router docs/PERMISSIONS.md § 4 and § 5): the node's owner, or an Owner
 * or Admin of the node's org. Only a controller turns the node's local MCP on or off (§ 8).
 *
 * The router enforces it: `node_grant` / `node_revoke` gate on `requireNodeManage` (router PR
 * #1832, router#1756). Staff never controls a customer node, and has no role in the org here.
 *
 * It is the same rule as `mayRemove` today. It has its own name because the two rules are
 * different decisions, and one can change without the other.
 */
export const controlsNode = controls

/**
 * Whether `role`, as `userId`, may ADD a node to a network: provision into it or register into it
 * (docs/PERMISSIONS.md § 5 Attachment, decided Q-4, app#376). The network's owner or an org
 * Owner/Admin always may; any other member only while the network's "members may add nodes" is on.
 *
 * ⚠ `membersMayAddNodes` undefined is ON. It is the column default, and a database without router
 * migration `0230` has no such column, which is the rule the router applied before the setting.
 * The router's `RequireAttach` asks the same rule; this only hides the buttons.
 */
export const mayAddNodes = (
  role: OrgRole,
  networkOwnerId: string | null | undefined,
  userId: string | null | undefined,
  membersMayAddNodes: boolean | null | undefined
): boolean => {
  if (!role) return false
  if (controls(role, networkOwnerId, userId)) return true
  return membersMayAddNodes !== false
}
